import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import CombinedBillCollection from "../../../modules/common/CombinedBillCollection";
import { centralReportingDataActions } from "../../../_actions";

/**
 * Map a `centralReportingData` row into the normalized `data` shape that
 * <CombinedBillCollection /> expects. Kept pure + exported for reuse/testing.
 */
export function mapReportingRowToData(row) {
  if (!row) return {};
  return {
    appID: row.appointment_id || row.app_id || row.appId || row._id || "",
    leadId: row.lead_id || row.leadId || "",
    lead_id: row.lead_id || row.leadId || "",
    collectionStatus: row.collectionStatus || "",
    payment_mode: row.payment_mode || "",
    name: row.patient_name || "",
    patient_name: row.patient_name || "",
    hospital: row.hospitalName?.value || row.hospital || "",
    hospital_name: row.hospitalName?.value || "",
    city: row.city?.value || row.city || "",
    room: row.room || "",
    tabKey: "Final Bill",
    ipdNoOnBill: row.ipdNoOnBill?.value || row.ipdNoOnBill || "",
    finalBillObj: row.finalBillObj || {},
    finalBillAmount: row.finalBillObj?.finalBillAmount ?? row.finalBillAmount ?? null,
    netBillAmount: row.finalBillObj?.netBillAmount ?? row.netBillAmount ?? null,
    urlArray: row.urlArray || row.finalBillCentral?.images || [],
    discharge_date: row.discharge_date || "",
    actual_admission_date: row.actual_admission_date || "",
    actualOtStartTime: row.actualOtStartTime || "",
    actualOtEndTime: row.actualOtEndTime || "",
    totalCollectibleAtAdmission: row.totalCollectibleAtAdmission || "",
    totalCollectibleAtDischarge: row.totalCollectibleAtDischarge || "",
    totalCollectedAtAdmission: row.totalCollectedAtAdmission || "",
    totalCollectedAtDischarge: row.totalCollectedAtDischarge || "",
  };
}

/**
 * Central-module wrapper for the Combined Bill & Collection Actions accordion.
 *
 * Reads the active patient's record from `centralReportingData`, maps it into the
 * normalized `data` shape, and renders the shared inline accordion so it sits within
 * the patient-details page (mobile-friendly). This keeps the shared component
 * decoupled from the central Redux slice.
 */
const CentralCombinedBillCollection = () => {
  const dispatch = useDispatch();
  const reportingData = useSelector(
    (state) => state.centralReportingData?.reportingData
  );

  // reportingData can arrive in two shapes depending on which action populated it.
  const row =
    reportingData?.data?.[0] || reportingData?.reportingData?.data?.[0];

  const data = useMemo(() => mapReportingRowToData(row), [row]);

  const refreshReporting = () => {
    if (data.appID) {
      dispatch(
        centralReportingDataActions.getCentralReportingData({
          search_query: data.appID,
        })
      );
    }
  };

  // Render as soon as reporting data exists; the accordion body handles its own
  // empty/loading state (keeps it consistent with the sibling accordions).
  if (!reportingData) return null;

  return (
    <CombinedBillCollection
      data={data}
      onRefresh={refreshReporting}
      onUpdateIpdNo={() => {}}
      onUpdateInfo={() => {}}
      onClose={() => {}}
    />
  );
};

export default CentralCombinedBillCollection;
